import { useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  type CourseRules,
  type CourseRulesDraft,
  type CreditBenefit,
  getCourseAccess,
  getCourseRules,
  getCreditTypes,
  previewCourseAccessRevocation,
  REGULAR_CREDIT_TYPE,
  type RevocationPreview,
  setCourseAccess,
} from '@/lib/credits-api'
import { getErrorMessage } from '@/lib/error-messages'
import { formatDateTime } from '@/lib/date'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

export function CreditTypeSelect(
  { value, onChange, id, disabled }: {
    value: string
    onChange: (value: string) => void
    id?: string
    disabled?: boolean
  },
) {
  const types = useQuery({ queryKey: ['credit-types'], queryFn: getCreditTypes })
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger id={id}>
        <SelectValue placeholder='Credit-Art' />
      </SelectTrigger>
      <SelectContent>
        {(types.data ?? []).filter((type) => type.is_active || type.id === value).map((type) => (
          <SelectItem key={type.id} value={type.id}>
            {type.name}
            {!type.is_active ? ' (inaktiv)' : ''}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export function CourseRulesEditor({ value, onChange, termsError, children }: {
  value: CourseRulesDraft
  onChange: (value: CourseRulesDraft) => void
  termsError?: string
  children: ReactNode
}) {
  return (
    <section className='space-y-4 rounded-lg border p-4'>
      <h3 className='font-semibold'>Credits und Teilnahme</h3>
      <div className='space-y-2'>
        <Label>Credit-Art</Label>
        <CreditTypeSelect
          value={value.credit_type_id}
          onChange={(credit_type_id) => onChange({ ...value, credit_type_id })}
        />
      </div>
      {children}
      <div className='space-y-2'>
        <Label htmlFor='course-guests'>Gäste je Buchung</Label>
        <Input
          id='course-guests'
          type='number'
          min={0}
          max={20}
          value={value.max_guests_per_booking}
          onChange={(event) =>
            onChange({ ...value, max_guests_per_booking: Number(event.target.value) })}
        />
        <p className='text-xs text-muted-foreground'>
          0 deaktiviert Gasteinladungen. Offene und angenommene Einladungen zählen gegen das Limit.
          Gäste belegen keinen Teilnehmerplatz und verbrauchen keine Credits. Für Termine mit nur
          einem Hauptbucher und seinen Gästen die maximale Teilnehmerzahl auf 1 setzen.
        </p>
      </div>
      {([
        ['requires_approval', 'Administrative Freischaltung erforderlich'],
        ['requires_terms', 'Akzeptieren der Kurs-AGB vor Buchung erforderlich'],
      ] as const).map(([field, label]) => (
        <label key={field} className='flex items-center gap-3 text-sm'>
          <Checkbox
            checked={value[field]}
            onCheckedChange={(checked) => onChange({ ...value, [field]: checked === true })}
          />
          {label}
        </label>
      ))}
      <div className='space-y-2'>
        <Label htmlFor='course-terms'>Kurs-AGB</Label>
        <Textarea
          id='course-terms'
          rows={7}
          value={value.terms_body}
          aria-invalid={termsError ? true : undefined}
          onChange={(event) => onChange({ ...value, terms_body: event.target.value })}
        />
        {termsError && <p className='text-sm font-medium text-destructive'>{termsError}</p>}
        <p className='text-xs text-muted-foreground'>
          Jede Textänderung speichert eine neue Version. Ist die Zustimmung erforderlich, bestätigen
          Teilnehmer die aktuelle Version einmalig vor der Buchung oder Einladungsannahme; nach
          einer Änderung erneut. Ohne Häkchen wird der Text nur gespeichert.
        </p>
      </div>
    </section>
  )
}

export function PackageBenefitsEditor({ benefits, requiredCourseId, billingMode, onChange }: {
  benefits: CreditBenefit[]
  requiredCourseId: string | null
  billingMode: 'payment' | 'subscription'
  onChange: (benefits: CreditBenefit[], requiredCourseId: string | null) => void
}) {
  const courses = useQuery({ queryKey: ['course-rules'], queryFn: getCourseRules })
  const requiredCourse = requiredCourseId ?? 'none'
  const setBenefits = (value: CreditBenefit[]) => onChange(value, requiredCourseId)
  const setRequiredCourse = (value: string) => onChange(benefits, value === 'none' ? null : value)
  function change(index: number, value: Partial<CreditBenefit>) {
    setBenefits(
      benefits.map((entry, position) => position === index ? { ...entry, ...value } : entry),
    )
  }
  return (
    <section className='space-y-4 rounded-lg border p-4'>
      <h3 className='font-semibold'>Enthaltene Leistungen</h3>
      {benefits.map((benefit, index) => (
        <div key={index} className='space-y-2 rounded-md border p-3'>
          <CreditTypeSelect
            value={benefit.credit_type_id}
            onChange={(value) =>
              change(index, { credit_type_id: value })}
          />
          {billingMode === 'subscription' && (
            <Select
              value={benefit.kind}
              onValueChange={(value: 'weekly' | 'unlimited') =>
                change(index, {
                  kind: value,
                  amount: value === 'unlimited' ? null : benefit.amount ?? 1,
                })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='weekly'>Credits pro Woche</SelectItem>
                <SelectItem value='unlimited'>Unlimited</SelectItem>
              </SelectContent>
            </Select>
          )}
          {benefit.kind !== 'unlimited' && (
            <Input
              aria-label='Enthaltene Credits'
              type='number'
              step='0.1'
              min='0.1'
              value={benefit.amount ?? ''}
              onChange={(event) => change(index, { amount: Number(event.target.value) })}
            />
          )}
          <Button
            type='button'
            variant='ghost'
            onClick={() =>
              setBenefits(benefits.filter((_, position) => position !== index))}
          >
            Leistung entfernen
          </Button>
        </div>
      ))}
      <Button
        type='button'
        variant='outline'
        onClick={() =>
          setBenefits([...benefits, {
            credit_type_id: REGULAR_CREDIT_TYPE,
            kind: billingMode === 'payment' ? 'balance' : 'weekly',
            amount: 1,
          }])}
      >
        Leistung hinzufügen
      </Button>
      <div className='space-y-2'>
        <Label>Kauf setzt Kursfreischaltung voraus</Label>
        <Select value={requiredCourse} onValueChange={setRequiredCourse}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='none'>Keine Freischaltung notwendig</SelectItem>
            {courses.data?.filter((course) => course.requires_approval).map((course) => (
              <SelectItem key={course.id} value={course.id}>{course.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <p className='text-xs text-muted-foreground'>
        Änderungen gelten für neue Abschlüsse. Bestandsabos behalten ihre Konditionen, bis eine
        Übernahme zur nächsten Abrechnung ausdrücklich gewählt wird. Gekaufte Guthaben bleiben erhalten.
      </p>
    </section>
  )
}

export function CustomerCourseAccess({ userId }: { userId: string }) {
  const client = useQueryClient()
  const courses = useQuery({ queryKey: ['course-rules'], queryFn: getCourseRules })
  const access = useQuery({
    queryKey: ['course-access', userId],
    queryFn: () => getCourseAccess(userId),
  })
  const [preview, setPreview] = useState<{ course: CourseRules; impact: RevocationPreview } | null>(
    null,
  )
  const mutation = useMutation({
    mutationFn: (
      { courseId, approved, confirmation }: {
        courseId: string
        approved: boolean
        confirmation?: string
      },
    ) =>
      setCourseAccess(userId, courseId, approved, confirmation),
    onSuccess: () => {
      setPreview(null)
      void client.invalidateQueries()
      toast.success('Freischaltung aktualisiert.')
    },
    onError: async (error) => {
      if (error instanceof Error && error.message.includes('ACCESS_PREVIEW_CHANGED') && preview) {
        const impact = await previewCourseAccessRevocation(userId, preview.course.id)
        setPreview({ course: preview.course, impact })
      }
      toast.error(getErrorMessage(error))
    },
  })
  const approvableCourses = courses.data?.filter((course) => course.requires_approval) ?? []
  return (
    <div className='space-y-3'>
      {courses.isLoading || access.isLoading ? (
        <p className='text-sm text-muted-foreground'>Freischaltungen werden geladen ...</p>
      ) : approvableCourses.length === 0 ? (
        <p className='text-sm text-muted-foreground'>
          Es gibt keine Kurse, die eine Freischaltung erfordern.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Kurs</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Geändert am</TableHead>
              <TableHead className='text-right'>Aktion</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {approvableCourses.map((course) => {
              const entry = access.data?.find((item) => item.course_id === course.id)
              const approved = entry?.is_approved === true
              return (
                <TableRow key={course.id}>
                  <TableCell className='font-medium'>{course.name}</TableCell>
                  <TableCell>
                    <Badge variant={approved ? 'default' : 'secondary'}>
                      {approved ? 'Freigeschaltet' : 'Nicht freigeschaltet'}
                    </Badge>
                  </TableCell>
                  <TableCell>{entry ? formatDateTime(entry.updated_at) : '–'}</TableCell>
                  <TableCell>
                    <div className='flex justify-end'>
                      <Button
                        type='button'
                        size='sm'
                        variant={approved ? 'destructive' : 'outline'}
                        disabled={mutation.isPending}
                        onClick={async () => {
                          if (!approved) {
                            mutation.mutate({ courseId: course.id, approved: true })
                            return
                          }
                          try {
                            const impact = await previewCourseAccessRevocation(userId, course.id)
                            setPreview({ course, impact })
                          } catch (error) {
                            toast.error(getErrorMessage(error))
                          }
                        }}
                      >
                        {approved ? 'Freischaltung entziehen' : 'Freischalten'}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}
        <Dialog
          open={preview !== null}
          onOpenChange={(open) => {
            if (!open) setPreview(null)
          }}
        >
          <DialogContent className='max-h-[90dvh] overflow-y-auto'>
            <DialogHeader>
              <DialogTitle>Freischaltung für {preview?.course.name} entziehen?</DialogTitle>
              <DialogDescription>
                Die folgenden Buchungen und Gastteilnahmen werden beendet. Guthaben wird
                entsprechend seiner Quelle erstattet.
              </DialogDescription>
            </DialogHeader>
            <div className='space-y-3 text-sm'>
              <p className='font-semibold'>{preview?.impact.bookings.length ?? 0} Hauptbuchungen</p>
              {preview?.impact.bookings.map((booking) => (
                <p key={booking.id}>
                  {booking.course_name}: {new Date(booking.starts_at).toLocaleString('de-AT')}
                </p>
              ))}
              <p className='font-semibold'>
                {preview?.impact.guests.length ?? 0} Gastteilnahmen / Einladungen
              </p>
              {preview?.impact.guests.map((guest) => (
                <p key={guest.id}>
                  {guest.name} · {new Date(guest.starts_at).toLocaleString('de-AT')} ·{' '}
                  {guest.status === 'accepted' ? 'angenommen' : 'ausstehend'}
                </p>
              ))}
              <p className='font-semibold'>Laufende Abos bleiben unverändert</p>
              {preview?.impact.subscriptions.map((subscription) => (
                <p key={subscription.id}>{subscription.name}</p>
              ))}
              <p>
                Preise und Verträge werden hier nicht geändert. Passe betroffene Abos gegebenenfalls
                separat an.
              </p>
            </div>
            <DialogFooter>
              <Button variant='outline' onClick={() => setPreview(null)}>Abbrechen</Button>
              <Button
                variant='destructive'
                disabled={mutation.isPending}
                onClick={() => {
                  if (preview) {
                    mutation.mutate({
                      courseId: preview.course.id,
                      approved: false,
                      confirmation: preview.impact.confirmation,
                    })
                  }
                }}
              >
                Freischaltung entziehen und Teilnahmen beenden
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
    </div>
  )
}
